// Évaluations (M11), réservées aux RH habilités : campagnes à grille figée, préparation d'une évaluation
// (notes par critère et commentaire, chiffrés au repos), validation qui la fige et la rend visible de
// l'agent lié à un compte. Une évaluation préparatoire n'est jamais montrée à l'agent évalué.
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, LienBouton, Panneau, PuceEtat, Selection, Tableau, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, ClipboardCheck, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerCampagne, INVALIDER_PERSONNEL, preparerEvaluation, useAgents, useCampagnes, useEvaluation, useEvaluationsCampagne, validerEvaluation, type Campagne, type Critere, type EvaluationListe } from './api';
import { aujourdhuiIso } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

/** Grille saisie à raison d'un critère par ligne : code;libellé;maximum. */
export function lireGrille(texte: string): Critere[] | null {
  const lignes = texte.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const grille = lignes.map((l) => l.split(';').map((x) => x.trim())).map(([code = '', libelle = '', maximum = '']) => ({ code, libelle, maximum: Number(maximum) }));
  return grille.length && grille.every((c) => /^[a-z][a-z0-9_]{0,39}$/.test(c.code) && c.libelle && Number.isInteger(c.maximum) && c.maximum >= 1 && c.maximum <= 100) ? grille : null;
}

function DialogueCampagne({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({ libelle: '', annee: Number(aujourdhuiIso().slice(0, 4)), grille: '' });
  const grille = lireGrille(v.grille);
  const action = useAction({ executer: () => creerCampagne({ libelle: v.libelle, annee: v.annee, grille }), succes: 'Campagne ouverte ; sa grille est figée.', invalider: INVALIDER_PERSONNEL, apres: onFermer });
  return (
    <Dialogue ouvert titre="Nouvelle campagne d'évaluation" description="La grille n'est plus modifiable une fois la campagne ouverte." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!grille || !v.libelle.trim()} onClick={() => action.lancer(undefined)}>Ouvrir la campagne</Bouton></>}>
      <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} required />
      <Champ libelle="Année" type="number" min={2000} max={2200} value={v.annee} onChange={(e) => setV((x) => ({ ...x, annee: Number(e.target.value) }))} required />
      <ZoneTexte libelle="Grille : un critère par ligne, sous la forme code;libellé;maximum" value={v.grille} onChange={(e) => setV((x) => ({ ...x, grille: e.target.value }))} required rows={5} />
      {v.grille && !grille ? <Bandeau type="attention">Grille incomplète : code en minuscules, libellé et maximum entier de 1 à 100 sur chaque ligne.</Bandeau> : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function DialoguePreparation({ campagne, agentId, onFermer }: { campagne: Campagne; agentId?: string; onFermer: () => void }) {
  const agents = useAgents('', !agentId);
  const [agent, setAgent] = useState(agentId ?? '');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [commentaire, setCommentaire] = useState('');
  const action = useAction({
    executer: () => preparerEvaluation({ campagne_id: campagne.id, agent_id: agent, commentaire, notes: Object.fromEntries(Object.entries(notes).filter(([, n]) => n !== '').map(([c, n]) => [c, Number(n)])) }),
    succes: 'Évaluation préparatoire enregistrée ; elle reste invisible de l\'agent jusqu\'à sa validation.',
    invalider: INVALIDER_PERSONNEL,
    apres: onFermer,
  });
  return (
    <Dialogue ouvert titre={`Préparer une évaluation : ${campagne.libelle}`} onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!agent} onClick={() => action.lancer(undefined)}>Enregistrer</Bouton></>}>
      {agentId ? null : <Selection libelle="Agent" options={(agents.data?.elements ?? []).map((a) => ({ valeur: a.id, libelle: `${a.nom} ${a.prenoms} (${a.matricule})` }))} optionVide="Choisir un agent" value={agent} onChange={(e) => setAgent(e.target.value)} />}
      <div className={styles.grilleFormulaire}>
        {campagne.grille.map((c) => <Champ key={c.code} libelle={`${c.libelle} (sur ${c.maximum})`} type="number" min={0} max={c.maximum} value={notes[c.code] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [c.code]: e.target.value }))} facultatif />)}
      </div>
      <ZoneTexte libelle="Commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} facultatif rows={4} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function FicheEvaluation({ id, onFermer }: { id: string; onFermer: () => void }) {
  const { contexte } = useSession();
  const e = useEvaluation(id);
  const valider = useAction({ executer: () => validerEvaluation(id), succes: 'Évaluation validée : elle est figée et visible de l\'agent.', invalider: INVALIDER_PERSONNEL });
  const d = e.data;
  return (
    <Dialogue ouvert titre="Évaluation" onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Fermer</Bouton>{d?.etat === 'preparation' && peut(contexte, 'personnel_sensible', 'modifier') ? <Bouton variante="principal" chargement={valider.enCours} onClick={() => valider.lancer(undefined)}>Valider l&apos;évaluation</Bouton> : null}</>}>
      <EtatRequete chargement={e.isPending} erreur={e.error} lot="L5-A" fonction="L'évaluation">
        {d ? (
          <dl className={commun.definitions}>
            <dt>Campagne</dt><dd>{d.campagne} ({d.annee})</dd>
            {d.grille.map((c) => <div key={c.code}><dt>{c.libelle}</dt><dd>{d.notes[c.code] !== undefined ? `${d.notes[c.code]} sur ${c.maximum}` : 'non notée'}</dd></div>)}
            <dt>Commentaire</dt><dd>{d.commentaire || 'aucun'}</dd>
            <dt>État</dt><dd>{d.etat === 'validee' ? `Validée le ${date(d.validee_le)}` : 'En préparation'}</dd>
          </dl>
        ) : null}
      </EtatRequete>
      {valider.messageErreur ? <Bandeau type="erreur">{valider.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function EvaluationsCampagne({ campagne }: { campagne: Campagne }) {
  const { contexte } = useSession();
  const liste = useEvaluationsCampagne(campagne.id);
  const [fiche, setFiche] = useState<string | null>(null);
  const [preparation, setPreparation] = useState<{ agent?: string } | null>(null);
  const colonnes: ColonneTableau<EvaluationListe>[] = [
    { id: 'agent', entete: 'Agent', enTeteLigne: true, cellule: (x) => `${x.nom} ${x.prenoms} (${x.matricule})` },
    { id: 'etat', entete: 'État', cellule: (x) => <PuceEtat ton={x.etat === 'validee' ? 'ok' : 'attente'} libelle={x.etat === 'validee' ? 'Validée' : 'En préparation'} /> },
    { id: 'action', entete: 'Action', cellule: (x) => (
      <span>
        <Bouton variante="secondaire" onClick={() => setFiche(x.id)}>Ouvrir</Bouton>{' '}
        {x.etat === 'preparation' && peut(contexte, 'personnel_sensible', 'creer') ? <Bouton variante="secondaire" onClick={() => setPreparation({ agent: x.agent_id })}>Compléter</Bouton> : null}
      </span>
    ) },
  ];
  return (
    <Panneau titre={`${campagne.libelle} (${campagne.annee})`} niveau={2} actions={peut(contexte, 'personnel_sensible', 'creer') ? <Bouton variante="secondaire" onClick={() => setPreparation({})}>Préparer une évaluation</Bouton> : null}>
      <p>Grille : {campagne.grille.map((c) => `${c.libelle} (sur ${c.maximum})`).join(', ')}. {campagne.en_preparation} en préparation, {campagne.validees} validée(s).</p>
      <EtatRequete chargement={liste.isPending} erreur={liste.error} lot="L5-A" fonction="Les évaluations de la campagne">
        {(liste.data?.elements ?? []).length ? <Tableau legende={`Évaluations : ${campagne.libelle}`} colonnes={colonnes} lignes={liste.data?.elements ?? []} cleLigne={(x) => x.id} /> : <p>Aucune évaluation préparée.</p>}
      </EtatRequete>
      {fiche ? <FicheEvaluation id={fiche} onFermer={() => setFiche(null)} /> : null}
      {preparation ? <DialoguePreparation campagne={campagne} agentId={preparation.agent} onFermer={() => setPreparation(null)} /> : null}
    </Panneau>
  );
}

export function PageEvaluations() {
  const { contexte } = useSession();
  const campagnes = useCampagnes();
  const [creation, setCreation] = useState(false);
  return (
    <>
      <EnTetePage etiquette="Personnel" titre="Évaluations" description="Campagnes à grille versionnée ; l'agent ne voit son évaluation qu'après validation."
        actions={<><LienBouton href="/personnel" icone={ArrowLeft} variante="secondaire">Personnel</LienBouton>{peut(contexte, 'personnel_sensible', 'modifier') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvelle campagne</Bouton> : null}</>} />
      <EtatRequete chargement={campagnes.isPending} erreur={campagnes.error} onReessayer={() => void campagnes.refetch()} lot="L5-A" fonction="Les campagnes d'évaluation">
        {(campagnes.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucune campagne" icone={ClipboardCheck}><p>Aucune campagne d&apos;évaluation n&apos;est ouverte.</p></EtatVide>
          : <div className={commun.pile}>{(campagnes.data?.elements ?? []).map((c) => <EvaluationsCampagne key={c.id} campagne={c} />)}</div>}
      </EtatRequete>
      {creation ? <DialogueCampagne onFermer={() => setCreation(false)} /> : null}
    </>
  );
}
